import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { create, getAll } from "../../api/API";
import { FcGoogle } from "react-icons/fc";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import { Row, Col } from "react-bootstrap";
import "react-phone-input-2/lib/style.css";
import "../../styles/Signup.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { GoogleLogin } from "@react-oauth/google";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { loginUser } from "../../utility/UserUtils";
import CompleteGoogleProfileForm from "./CompleteProfile";

const SignupForm = ({ onSignup }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    cohort: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Google signup states
  const [googleUser, setGoogleUser] = useState(null);
  const [showGoogleProfileForm, setShowGoogleProfileForm] = useState(false);

  // Fetch cohorts
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await getAll("/cohorts", { limit: 50 });
        const sorted = (res.docs || []).sort((a, b) => {
          const getStartYear = (range) => {
            if (!range) return Infinity;
            const match = range.match(/^(\d+)/);
            return match ? parseInt(match[1]) : Infinity;
          };
          return getStartYear(a.range) - getStartYear(b.range);
        });
        setCohorts(sorted);
      } catch (err) {
        console.error("Failed to load cohorts:", err);
        toast.error("Failed to load cohorts. Please try again.");
      }
    };
    fetchCohorts();
  }, []);

  const handleChange = (field, value, nestedField = null) => {
    if ((field === "firstName" || field === "lastName") && !nestedField) {
      // Allow only letters and spaces
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (nestedField) {
      setForm((prev) => ({
        ...prev,
        [field]: { ...prev[field], [nestedField]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validatePhone = (phone) => {
    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed || !parsed.isValid())
      return "Please enter a valid phone number with country code";
    return null;
  };

  const validate = () => {
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.phone ||
      !form.password ||
      !form.confirmPassword ||
      !form.cohort ||
      // !form.address.street ||
      !form.address.city ||
      // !form.address.state ||
      !form.address.zip
      // !form.address.country
    )
      return "All fields are required";
    const phoneError = validatePhone(form.phone);
    if (phoneError) return phoneError;
    if (form.password.length < 6)
      return "Password must be at least 6 characters";
    if (form.password !== form.confirmPassword) return "Passwords do not match";
    return null;
  };

  // -------------------- Email Signup --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errMsg = validate();
    if (errMsg) return toast.error(errMsg);

    try {
      setLoading(true);
      const res = await create("/members", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        cohort: form.cohort,
        address: form.address,
      });

      if (res.token && res.user) {
        loginUser(res.user, res.token);
        window.dispatchEvent(new Event("authChange"));
      }

      toast.success(
        "Signup successful! Please check your email for verification link."
      );

      if (onSignup) onSignup(res);
      navigate("/login");
    } catch (err) {
      const responseData = err.response?.data;
      let message = "Signup failed";

      if (responseData?.errors?.length > 0) {
        const firstError = responseData.errors[0];
        if (firstError?.data?.errors?.length > 0) {
          message = firstError.data.errors[0].message;
        } else if (firstError?.message) {
          message = firstError.message;
        }
      } else if (responseData?.message) {
        message = responseData.message;
      }

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  //Google Signup
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    const token = credentialResponse?.credential;
    if (!token) {
      toast.error("Google token missing. Try again.");
      return;
    }

    try {
      //  Try logging in (user already exists)
      const res = await create("/auth/google-login", { token });

      //  User exists -- Login immediately
      loginUser(res.user, res.token);
      window.dispatchEvent(new Event("authChange"));
      toast.success(`Welcome back, ${res.user.firstName || res.user.email}!`);
      navigate("/");
      return; // IMPORTANT
    } catch (err) {
      // STEP User does NOT exist → Proceed to complete profile page
      console.log("Google user not found → redirecting to complete profile");

      navigate("/auth/google/callback", {
        state: { token }, // pass token to complete-profile page
      });
    }
  };

  const handleGoogleLoginError = () => {
    toast.error("Google login failed. Please try again.");
    console.log("[GoogleLogin] Login Failed");
  };

  const handleGoogleProfileSubmit = async (extraData) => {
    try {
      setGoogleLoading(true);
      const payload = {
        token: googleUser.token,
        cohort: extraData.cohort,
        address: extraData.address,
      };
      const res = await create("auth/google-signup", payload);
      loginUser(res.user, res.token);
      window.dispatchEvent(new Event("authChange"));
      toast.success(`Welcome, ${res.user.firstName || res.user.email}!`);
      navigate("/");
    } catch (err) {
      console.error("Google signup failed:", err);
      toast.error("Google signup failed.");
    } finally {
      setGoogleLoading(false);
      setShowGoogleProfileForm(false);
    }
  };

  //LinkedIn Login
  // const handleLinkedInLogin = () => {
  //   const params = new URLSearchParams({
  //     response_type: "code",
  //     client_id: process.env.REACT_APP_LINKEDIN_CLIENT_ID,
  //     redirect_uri: process.env.REACT_APP_LINKEDIN_REDIRECT_URI,
  //     scope: "openid email profile",
  //   });
  //   window.location.href = `https://www.linkedin.com/oauth/v2/authorization?${params}`;

  //   console.log("[LinkedInLogin] Starting LinkedIn OAuth flow");
  //   console.log("[LinkedInLogin] Redirect URI:", redirectUri);
  //   console.log("[LinkedInLogin] Client ID:", clientId);

  //   const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
  //     redirectUri
  //   )}&scope=openid%20profile%20email`;

  //   console.log(
  //     "[LinkedInLogin] Redirecting user to LinkedIn:",
  //     linkedinAuthUrl
  //   );

  //   window.location.href = linkedinAuthUrl;
  // };

  return (
    <div
      className="auth-box bg-white"
      style={{ width: "100%", maxWidth: "100%" }}
    >
      {/*EMAIL SIGNUP FORM*/}
      <form onSubmit={handleSubmit}>
        <h2 className="auth-title text-center">Sign up with email</h2>
        <p className="auth-subtext text-black text-center">
          *Required fields are marked with an asterisk
        </p>

        {/* Name */}
        <Row className="mb-3">
          <Col md={6}>
            <FloatingLabelInput
              label="First name*"
              type="text"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <FloatingLabelInput
              label="Last name*"
              type="text"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </Col>
        </Row>

        {/* Email & Phone */}
        <Row className="mb-3">
          <Col md={6}>
            <FloatingLabelInput
              label="Email*"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </Col>
          <Col md={6}>
            <FloatingLabelInput
              label="Phone Number*"
              type="phone"
              value={form.phone}
              onChange={(val) => handleChange("phone", val)}
              clearValue={() => handleChange("phone", "")}
              containerStyle={{
                border: "1px solid #000",
                borderRadius: "8px",
                padding: 0,
                height: "65px",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
              }}
              inputStyle={{
                width: "100%",
                height: "56px",
                padding: "0 11px",
                fontSize: "16px",
                marginLeft: "-5px",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: 0,
                outline: "none",
              }}
              buttonStyle={{
                border: "none",
                backgroundColor: "transparent",
                height: "56px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
                width: "58px",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
                boxShadow: "none",
              }}
              dropdownStyle={{
                borderRadius: "8px",
                boxShadow: "none",
              }}
            />
          </Col>
        </Row>

        {/* Password */}
        <Row className="mb-3">
          <Col md={6} className="position-relative">
            <FloatingLabelInput
              label="Password*"
              type={showPwd ? "text" : "password"}
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
            <span
              className="password-icon"
              onClick={() => setShowPwd(!showPwd)}
            >
              {showPwd ? <FaEyeSlash /> : <FaEye />}
            </span>
          </Col>
          <Col md={6} className="position-relative">
            <FloatingLabelInput
              label="Confirm Password*"
              type={showConfirmPwd ? "text" : "password"}
              value={form.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
            />
            <span
              className="password-icon"
              onClick={() => setShowConfirmPwd(!showConfirmPwd)}
            >
              {showConfirmPwd ? <FaEyeSlash /> : <FaEye />}
            </span>
          </Col>
        </Row>

        {/* Address */}
        <Row className="mb-3">
          <Col md={12}>
            <FloatingLabelInput
              label="Street"
              type="text"
              value={form.address.street}
              onChange={(e) =>
                handleChange("address", e.target.value, "street")
              }
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <FloatingLabelInput
              label="City*"
              type="text"
              value={form.address.city}
              onChange={(e) => handleChange("address", e.target.value, "city")}
            />
          </Col>
          <Col md={4}>
            <FloatingLabelInput
              label="State"
              type="text"
              value={form.address.state}
              onChange={(e) => handleChange("address", e.target.value, "state")}
            />
          </Col>
          <Col md={4}>
            <FloatingLabelInput
              label="ZIP Code*"
              type="text"
              value={form.address.zip}
              onChange={(e) => handleChange("address", e.target.value, "zip")}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <FloatingLabelInput
              label="Country"
              type="text"
              value={form.address.country}
              onChange={(e) =>
                handleChange("address", e.target.value, "country")
              }
            />
          </Col>
        </Row>

        {/* Cohort Selection */}
        <div className="mb-3 cohort-section">
          <h6>SELECT YOUR COHORT*</h6>
          <div className="cohort-grid">
            {cohorts.map((c) => (
              <div
                key={c.id}
                className={`cohort-box ${
                  form.cohort === c.id ? "selected" : ""
                }`}
                onClick={() => handleChange("cohort", c.id)}
              >
                <div className="cohort-title">{c.title}</div>
                <div className="cohort-range">{c.range}</div>
                <div className="cohort-stage">{c.stage}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="auth-btn bg-primary text-white"
          disabled={loading || googleLoading}
        >
          {loading
            ? "Signing up..."
            : googleLoading
            ? "Signing in with Google..."
            : "Join today"}
        </button>

        {/* OAuth */}
        <div className="auth-divider black">Or</div>
        <div className="auth-socials bg-white text-center">
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={handleGoogleLoginError}
            useOneTap={false}
            scope="openid profile email"
            prompt="consent"
            context="signup"
            ux_mode="popup"
          />
        </div>

        <p className="auth-footer">Already have an account?</p>
        <p>
          <Link to="/login">Login</Link>
        </p>
      </form>

      {/*Google Profile Completion Modal*/}
      {showGoogleProfileForm && googleUser && (
        <div className="google-profile-overlay">
          <CompleteGoogleProfileForm
            googleUser={googleUser}
            onSubmit={handleGoogleProfileSubmit}
            onCancel={() => setShowGoogleProfileForm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default SignupForm;
